import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';
import {
  Store,
  Rss,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Check,
  AlertTriangle,
  Info,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Spinner } from '@/components/ui/spinner';
import type { ContextTreeAccount, ContextTreeFeed, ContextTreeMc } from '@/hooks/useContextTree';
import { formatFeedBadge } from '@/lib/feedLabels';
import { formatInteger } from '@/lib/language';
import { flattenTree, matchesQuery } from './switcherSearch';
import {
  partitionMerchantCenters,
  duplicateMcNames,
  mcDisplayName,
  mcNameKey,
  namesInSeveralAccounts,
} from './switcherGroups';

const SYNC_FRESH_MS = 24 * 60 * 60 * 1000;

export interface FeedSwitcherCloseOptions {
  /**
   * Default true (Fokus zurueck auf die Pille). false fuer den Navigate-Pfad
   * ("Konten verwalten", Fix-Runde 2 M7): dort war die Pille nach der
   * Navigation zu /settings gar nicht mehr das sinnvolle Fokusziel — der per
   * requestAnimationFrame geplante Fokus-Restore griff trotzdem noch NACH dem
   * Routenwechsel und riss den Fokus zurueck auf die (jetzt fremde) Pille.
   */
  restoreFocus?: boolean;
}

interface FeedSwitcherListProps {
  accounts: ContextTreeAccount[];
  isLoading: boolean;
  isError: boolean;
  /** Neben-Abfragen `feed_product_counts` / `get_uploaded_feed_ids` gescheitert, der Baum steht. */
  detailsFailed?: boolean;
  selectedMerchantCenterId: string | null;
  selectedFeedId: string | null;
  /** true = gewechselt (Fokus zurueck, Popover schliessen); false = Speicher-Tor rot. */
  onSelect: (accountId: string, mcId: string, feedId: string) => Promise<boolean>;
  /** Nach erfolgreichem Wechsel UND nach "Konten verwalten". */
  onClose: (options?: FeedSwitcherCloseOptions) => void;
  autoFocusInput?: boolean;
}

function gmcDataSourcesUrl(externalId: string): string {
  return `https://merchants.google.com/mc/products/sources?a=${externalId}`;
}

function accountLabel(acc: ContextTreeAccount): string {
  return acc.displayName || acc.email || '';
}

function accountInitial(acc: ContextTreeAccount): string {
  const label = accountLabel(acc).trim();
  return label ? label.charAt(0).toUpperCase() : '?';
}

function feedSyncWarning(
  feed: ContextTreeFeed,
  language: string,
): { timeAgo: string } | null {
  const ts = feed.lastSyncedAt || feed.updatedAt;
  if (!ts) return null;
  const ageMs = Date.now() - new Date(ts).getTime();
  if (ageMs < SYNC_FRESH_MS) return null;
  const timeAgo = formatDistanceToNow(new Date(ts), {
    addSuffix: false,
    locale: language === 'de' ? de : enUS,
  });
  return { timeAgo };
}

interface FeedRowContentProps {
  feed: ContextTreeFeed;
  isCurrent: boolean;
  isBusy: boolean;
  language: string;
  t: TFunction;
}

/**
 * Der gemeinsame Signal-Cluster einer Feed-Zeile: Badge, Produktzahl,
 * Upload-Haken, aktueller-Feed-Haken, Wechsel-Spinner. Geteilt zwischen
 * Baum- und Suchzeile (Fix-Runde 1, Punkt 8) — vorher hatte die Suchzeile
 * NUR Name + Pfad, alle anderen Signale gingen beim Tippen einer Suchanfrage
 * verloren, obwohl genau dieselben Feeds gemeint sind. Die Sync-Warnung
 * (`staleSync`) bleibt bewusst AUSSERHALB dieses Clusters und nur in der
 * Baumzeile — die Suchzeile zeigt bereits den Pfad als Kontext, ein zweiter
 * mehrzeiliger Warnhinweis pro Treffer waere dort Ueberladung.
 */
function FeedRowContent({ feed, isCurrent, isBusy, language, t }: FeedRowContentProps) {
  const badge = formatFeedBadge(feed.targetCountry, feed.contentLanguage);
  return (
    <>
      {badge && (
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide">{badge}</span>
      )}
      {feed.productCount !== null && (
        <span className="tabular-nums text-[13px] text-foreground/70">
          {formatInteger(feed.productCount, language)}
        </span>
      )}
      {feed.uploaded && (
        <span className="flex shrink-0 items-center" title={t('context.switcher.uploadedHint')}>
          <CheckCircle2 className="h-3 w-3 text-success" aria-hidden="true" />
          <span className="sr-only">{t('context.switcher.uploadedHint')}</span>
        </span>
      )}
      {isCurrent && (
        <span className="flex shrink-0 items-center" title={t('context.switcher.currentHint')}>
          <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
          <span className="sr-only">{t('context.switcher.currentHint')}</span>
        </span>
      )}
      {isBusy && (
        <span className="flex shrink-0 items-center gap-1">
          <Spinner size="sm" />
          {/* Punkt 7: `switching` lag bisher unbenutzt in den Locale-Dateien —
              ohne Text ist ein drehender Kreis fuer Screenreader stumm. */}
          <span className="sr-only">{t('context.switcher.switching')}</span>
        </span>
      )}
    </>
  );
}

export const FeedSwitcherList: React.FC<FeedSwitcherListProps> = ({
  accounts,
  isLoading,
  isError,
  detailsFailed = false,
  selectedMerchantCenterId,
  selectedFeedId,
  onSelect,
  onClose,
  autoFocusInput = true,
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(selectedMerchantCenterId ? [selectedMerchantCenterId] : []),
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState<Set<string>>(() => new Set());
  const scrolledToCurrentRef = useRef(false);

  const listRef = useRef<HTMLDivElement>(null);

  const isSearchMode = query.trim().length > 0;

  // A3-12: der aktive Feed lag bei 83 MCs rund 2.300 px unter dem Fenster.
  // Einmal je Oeffnen in den Blick holen, sobald der Baum steht.
  useEffect(() => {
    if (scrolledToCurrentRef.current || isSearchMode || !selectedFeedId || accounts.length === 0) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-testid="switcher-feed-${selectedFeedId}"]`);
    if (!row) return;
    row.scrollIntoView({ block: 'center' });
    scrolledToCurrentRef.current = true;
  }, [accounts, isSearchMode, selectedFeedId]);

  async function handleChoose(accountId: string, mcId: string, feedId: string) {
    setFailed(null);
    setBusy(feedId);
    try {
      const ok = await onSelect(accountId, mcId, feedId);
      if (ok) {
        onClose();
      } else {
        setFailed(feedId);
      }
    } catch {
      // Punkt 4: ein WERFENDES `onSelect` (z.B. setSelection schlaegt fehl)
      // liess die Zeile vorher dauerhaft im Busy-Spinner haengen, weil
      // `setBusy(null)` nie erreicht wurde. Ein Fehlschlag ist hier dasselbe
      // Bild wie ein rotes Speicher-Tor: Inline-Fehlertext, Popover offen.
      setFailed(feedId);
    } finally {
      setBusy(null);
    }
  }

  function toggleExpand(mcId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(mcId)) next.delete(mcId);
      else next.add(mcId);
      return next;
    });
  }

  /**
   * cmdk haelt den DOM-Fokus im Suchfeld; Pfeiltasten bewegen nur einen
   * internen "highlighted"-Zeiger (`data-selected`, siehe cmdk-Quelle). Der
   * Handler haengt darum NICHT am fokussierten Element, sondern liest das
   * aktuell markierte `[cmdk-item]` aus dem Listen-Container. ArrowLeft/Right
   * gehoeren nicht zu cmdks eigenem Tastensatz (nur ArrowUp/Down/Enter/Escape/
   * Home/End/ctrl+n/p/j/k) — der Handler auf dem AEUSSEREN Wrapper sieht das
   * Ereignis darum erst NACHDEM cmdk es (folgenlos) durchgelassen hat.
   */
  function handleTreeKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const selectedEl = listRef.current?.querySelector<HTMLElement>('[cmdk-item][data-selected="true"]');
    const mcId = selectedEl?.getAttribute('data-mc-id');
    if (!mcId) return;
    e.preventDefault();
    setExpanded((prev) => {
      const next = new Set(prev);
      if (e.key === 'ArrowRight') next.add(mcId);
      else next.delete(mcId);
      return next;
    });
  }

  const flatHits = useMemo(() => (isSearchMode ? flattenTree(accounts) : []), [isSearchMode, accounts]);

  function findAccount(id: string): ContextTreeAccount | undefined {
    return accounts.find((a) => a.id === id);
  }
  function findMc(accountId: string, mcId: string): ContextTreeMc | undefined {
    return findAccount(accountId)?.merchantCenters.find((m) => m.id === mcId);
  }

  const feedHits = isSearchMode
    ? flatHits
        .filter((h) => h.kind === 'feed' && matchesQuery(h.haystack, query))
        .map((h) => ({ hit: h, acc: findAccount(h.accountId), mc: findMc(h.accountId, h.mcId) }))
        .filter(
          (x): x is { hit: (typeof flatHits)[number]; acc: ContextTreeAccount; mc: ContextTreeMc } =>
            !!x.acc && !!x.mc,
        )
    : [];
  const mcHits = isSearchMode
    ? flatHits
        .filter((h) => h.kind === 'mc' && matchesQuery(h.haystack, query))
        .map((h) => ({ hit: h, acc: findAccount(h.accountId), mc: findMc(h.accountId, h.mcId) }))
        .filter(
          (x): x is { hit: (typeof flatHits)[number]; acc: ContextTreeAccount; mc: ContextTreeMc } =>
            !!x.acc && !!x.mc,
        )
    : [];

  function renderMc(acc: ContextTreeAccount, mc: ContextTreeMc, dupes: Set<string>) {
    const revoked = mc.accessStatus !== 'active';
    // Nur `expanded` entscheidet: dessen Ausgangszustand seedet die aktuell
    // gewaehlte MC bereits (useState-Initializer). Ein zusaetzliches
    // `|| mc.id === selectedMerchantCenterId` wuerde die aktuelle MC dauerhaft
    // offen erzwingen: weder Klick noch ArrowLeft koennten sie je zuklappen.
    const isExpanded = !revoked && expanded.has(mc.id);
    const hasUploadedFeed = mc.feeds.some((f) => f.uploaded);
    const showNoFeedNote = isExpanded && mc.feeds.length === 0 && mc.hiddenAutomaticCount === 0;
    const isDupe = dupes.has(mcNameKey(mc));
    return (
      <React.Fragment key={mc.id}>
        <CommandItem
          value={`mc:${mc.id}`}
          data-testid={`switcher-mc-${mc.id}`}
          data-mc-id={revoked ? undefined : mc.id}
          aria-expanded={isExpanded}
          disabled={revoked}
          className="gap-1.5"
          onSelect={() => {
            if (!revoked) toggleExpand(mc.id);
          }}
        >
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <Store className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
          <span className="truncate">{mcDisplayName(mc)}</span>
          {isDupe && <span className="shrink-0 font-mono text-[11px] text-foreground/60">{mc.externalId}</span>}
          {mc.isMca && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide">MCA</span>
          )}
          {revoked && (
            <span className="flex items-center gap-1 text-[11px] text-warning-ink">
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
              {t('context.switcher.mcAccessRevoked')}
            </span>
          )}
          {hasUploadedFeed && (
            <span className="flex shrink-0 items-center" title={t('context.switcher.uploadedHint')}>
              <CheckCircle2 className="h-3 w-3 text-success" aria-hidden="true" />
              <span className="sr-only">{t('context.switcher.uploadedHint')}</span>
            </span>
          )}
          <span className="ml-auto shrink-0 text-[13px] text-foreground/70">
            {t('context.switcher.feedCount', { count: mc.feeds.length })}
          </span>
        </CommandItem>

        {isExpanded &&
          mc.feeds.map((feed) => {
            const stale = feedSyncWarning(feed, i18n.language);
            const isCurrent = feed.id === selectedFeedId;
            return (
              <CommandItem
                key={feed.id}
                value={`feed:${feed.id}`}
                data-testid={`switcher-feed-${feed.id}`}
                className="pl-7 gap-1.5"
                onSelect={() => handleChoose(acc.id, mc.id, feed.id)}
              >
                <Rss className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className={`truncate ${isCurrent ? 'font-bold text-primary' : ''}`}>
                  {feed.displayName || feed.feedId}
                </span>
                {stale && (
                  // Der Icon-Ton (`text-warning-ink`) bleibt, weil das Dreieck selbst kein
                  // Text ist und AA-Kontrast nicht auf Icons greift. Der TEXT sitzt in
                  // `text-foreground/70`: `text-warning-ink` auf `bg-accent` (markierte
                  // Zeile) faellt im Light-Theme auf 4.11:1, unter AA 4.5:1 (Fix-Runde 2 I2).
                  <span className="flex items-center gap-1 text-[11px]">
                    <AlertTriangle className="h-3 w-3 shrink-0 text-warning-ink" aria-hidden="true" />
                    <span className="text-foreground/70">{t('context.staleSync', { time: stale.timeAgo })}</span>
                  </span>
                )}
                <FeedRowContent
                  feed={feed}
                  isCurrent={isCurrent}
                  isBusy={busy === feed.id}
                  language={i18n.language}
                  t={t}
                />
              </CommandItem>
            );
          })}

        {isExpanded && mc.hiddenAutomaticCount > 0 && (
          <div
            role="note"
            data-testid={`switcher-hidden-${mc.id}`}
            className="flex items-center gap-1.5 px-2 py-1 pl-7 text-[11px] text-muted-foreground"
          >
            <Info className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span>{t('context.feedHidden', { count: mc.hiddenAutomaticCount })}</span>
          </div>
        )}

        {showNoFeedNote && (
          <div
            role="note"
            data-testid={`switcher-no-feed-${mc.id}`}
            className="flex flex-wrap items-center gap-x-1.5 px-2 py-1 pl-7 text-[11px] text-warning-ink"
          >
            <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span>{t('context.noFeed.title')}</span>
            <a
              href={gmcDataSourcesUrl(mc.externalId)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium underline underline-offset-2 hover:text-foreground"
            >
              {t('context.noFeed.cta')}
              <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
            </a>
          </div>
        )}
      </React.Fragment>
    );
  }

  // A3-17: Kontoteil im Suchpfad nur, wenn der MC-Name in mehreren Konten steht.
  const sharedNames = useMemo(() => namesInSeveralAccounts(accounts), [accounts]);
  const searchPath = (acc: ContextTreeAccount, mc: ContextTreeMc): string => {
    const key = mcNameKey(mc);
    const name = mcDisplayName(mc);
    const withAccount = !key || sharedNames.has(key);
    const dupeInAccount = !!key && duplicateMcNames(acc.merchantCenters).has(key);
    return `${withAccount ? `${accountLabel(acc)} › ` : ''}${name}${dupeInAccount ? ` · ${mc.externalId}` : ''}`;
  };

  return (
    <div className="flex flex-col" onKeyDown={handleTreeKeyDown}>
      <Command
        shouldFilter={false}
        aria-label={t('context.switcher.popoverLabel')}
        // A3-12: die Tastaturmarkierung startete auf der ersten MC des ersten
        // Kontos. cmdk nimmt `defaultValue` als Startwert; bei einer Suche
        // waehlt es selbst den ersten Treffer.
        defaultValue={selectedFeedId ? `feed:${selectedFeedId}` : undefined}
      >
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder={t('context.switcher.searchPlaceholder')}
          autoFocus={autoFocusInput}
        />
        <CommandList ref={listRef} className="max-h-[360px]">
          {isLoading && accounts.length === 0 && (
            <div className="flex items-center justify-center gap-2 px-3 py-6 text-xs text-muted-foreground">
              <Spinner size="sm" />
            </div>
          )}
          {isError && (
            <div className="px-3 py-4 text-xs text-destructive">{t('context.switcher.loadFailed')}</div>
          )}
          {detailsFailed && !isError && (
            <div
              data-testid="switcher-details-failed"
              role="status"
              className="px-3 py-2 text-xs text-muted-foreground"
            >
              {t('context.switcher.detailsFailed')}
            </div>
          )}

          {!isSearchMode &&
            accounts.map((acc) => (
              <CommandGroup
                key={acc.id}
                heading={
                  <div className="flex items-center gap-2 py-0.5">
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground"
                      aria-hidden="true"
                    >
                      {accountInitial(acc)}
                    </span>
                    <span className="flex min-w-0 flex-col leading-tight">
                      <span className="truncate text-xs font-semibold text-foreground">{accountLabel(acc)}</span>
                      {acc.email && acc.displayName && (
                        <span className="truncate text-[11px] font-normal text-foreground/60">{acc.email}</span>
                      )}
                    </span>
                    <span className="ml-auto shrink-0 text-[11px] font-normal text-muted-foreground">
                      {t('context.switcher.mcCount', { count: acc.merchantCenters.length })}
                    </span>
                  </div>
                }
              >
                {(() => {
                  const { primary, inactive } = partitionMerchantCenters(acc.merchantCenters, selectedMerchantCenterId);
                  const dupes = duplicateMcNames(acc.merchantCenters);
                  const inactiveOpen = showInactive.has(acc.id);
                  return (
                    <>
                      {primary.map((mc) => renderMc(acc, mc, dupes))}
                      {inactive.length > 0 && (
                        <CommandItem
                          value={`inactive:${acc.id}`}
                          data-testid={`switcher-inactive-toggle-${acc.id}`}
                          aria-expanded={inactiveOpen}
                          className="gap-1.5 text-[12px] text-muted-foreground"
                          onSelect={() =>
                            setShowInactive((prev) => {
                              const next = new Set(prev);
                              if (next.has(acc.id)) next.delete(acc.id);
                              else next.add(acc.id);
                              return next;
                            })
                          }
                        >
                          {inactiveOpen ? (
                            <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          )}
                          {t('context.switcher.inactiveGroup', { count: inactive.length })}
                        </CommandItem>
                      )}
                      {inactiveOpen && inactive.map((mc) => renderMc(acc, mc, dupes))}
                    </>
                  );
                })()}
              </CommandGroup>
            ))}

          {isSearchMode && feedHits.length > 0 && (
            <CommandGroup heading={t('context.switcher.groupFeeds')}>
              {feedHits.map(({ hit, acc, mc }) => {
                const feed = mc.feeds.find((f) => f.id === hit.feedId);
                if (!feed) return null;
                const isCurrent = feed.id === selectedFeedId;
                return (
                  <CommandItem
                    key={`search-feed:${feed.id}`}
                    value={`search-feed:${feed.id}`}
                    data-testid={`switcher-search-feed-${feed.id}`}
                    className="gap-1.5"
                    onSelect={() => handleChoose(acc.id, mc.id, feed.id)}
                  >
                    <Rss className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className={`truncate ${isCurrent ? 'font-bold text-primary' : ''}`}>
                        {feed.displayName || feed.feedId}
                      </span>
                      <span className="truncate text-[11px] text-foreground/70">{searchPath(acc, mc)}</span>
                    </span>
                    <FeedRowContent
                      feed={feed}
                      isCurrent={isCurrent}
                      isBusy={busy === feed.id}
                      language={i18n.language}
                      t={t}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {isSearchMode && mcHits.length > 0 && (
            <CommandGroup heading={t('context.switcher.groupMerchantCenters')}>
              {mcHits.map(({ hit, acc, mc }) => (
                <CommandItem
                  key={`search-mc:${mc.id}`}
                  value={`search-mc:${hit.mcId}`}
                  data-testid={`switcher-search-mc-${mc.id}`}
                  className="gap-1.5"
                  onSelect={() => {
                    setExpanded((prev) => new Set(prev).add(mc.id));
                    // Liegt die MC in der eingeklappten Gruppe (ohne Feeds oder
                    // ohne Zugriff), waere sie nach dem Leeren der Suche
                    // unsichtbar und der Hinweis "Noch kein Feed" mit dem
                    // GMC-Link unerreichbar. Die Gruppe geht deshalb mit auf.
                    const { inactive } = partitionMerchantCenters(acc.merchantCenters, selectedMerchantCenterId);
                    if (inactive.some((m) => m.id === mc.id)) {
                      setShowInactive((prev) => new Set(prev).add(acc.id));
                    }
                    setQuery('');
                  }}
                >
                  <Store className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                  <span className="truncate">{mcDisplayName(mc)}</span>
                  <span className="ml-auto shrink-0 truncate text-[11px] text-foreground/70">
                    {accountLabel(acc)}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Nur im Suchmodus: shouldFilter=false heisst, cmdk zaehlt "0
              registrierte Items" auch waehrend des initialen Ladens oder bei
              einem Baum-Fehler (dort wird noch/gar nichts gerendert) — dort
              waere "Keine Treffer" eine falsche Diagnose fuer einen Zustand,
              der mit Suche nichts zu tun hat. */}
          {isSearchMode && <CommandEmpty>{t('context.switcher.noResults')}</CommandEmpty>}
        </CommandList>
      </Command>

      {/* Punkt 1: lag vorher INNERHALB des Browse-Zweigs (`!isSearchMode && …`)
          und war damit unsichtbar, sobald der Fehlschlag im Suchmodus passierte
          (Klick auf einen Suchtreffer bei rotem Speicher-Tor). Hier, ausserhalb
          von Browse/Suche, rendert er in BEIDEN Modi — solange `failed` gesetzt
          ist, unabhaengig davon, ob die fehlgeschlagene Zeile gerade sichtbar ist. */}
      {failed && (
        <p
          role="alert"
          data-testid={`switcher-failed-${failed}`}
          className="border-t border-border px-3 py-1.5 text-[11px] text-destructive"
        >
          {t('context.switcher.switchFailed')}
        </p>
      )}

      <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground">
        <span data-testid="switcher-legend" className="flex items-center gap-1 md:hidden">
          <CheckCircle2 className="h-3 w-3 text-success" aria-hidden="true" />
          {t('context.switcher.uploadedHint')}
        </span>
        <span data-testid="switcher-key-hints" className="hidden items-center gap-2 md:flex">
          <span className="flex items-center gap-1">
            {/* D35: dieses kbd traegt font-mono, wird also von IBM Plex Mono gerendert
                (tailwind.config.ts:125), nicht von der Body-Schrift. Der eigentliche Grund
                fuer das Icon ist trotzdem schriftunabhaengig: ein Glyph als GANZER Inhalt
                eines Elements haengt immer an der Zeichen-Coverage genau dieser Schrift,
                ein Icon nie. Der Fliesstext daneben ("navigieren") nennt die Tasten nicht
                einzeln, deshalb traegt jedes kbd seine eigene sr-only-Beschriftung, damit
                die Angabe fuer Screenreader nicht rein grafisch ist. */}
            <kbd className="rounded border border-border px-1 py-0.5 font-mono text-[10px]">
              <span className="sr-only">{t('context.switcher.keyUp')}</span>
              <ArrowUp aria-hidden="true" className="h-2.5 w-2.5" />
            </kbd>
            <kbd className="rounded border border-border px-1 py-0.5 font-mono text-[10px]">
              <span className="sr-only">{t('context.switcher.keyDown')}</span>
              <ArrowDown aria-hidden="true" className="h-2.5 w-2.5" />
            </kbd>
            {t('context.switcher.hintNavigate')}
          </span>
          <span className="flex items-center gap-1">
            {/* Fix-Runde 1: derselbe Fall wie bei den Pfeilen oben, nur bisher offen
                gelassen. Der Nachbartext ("auswaehlen") benennt die Enter-Taste ebenso
                wenig wie "navigieren" die Pfeile -- ohne sr-only-Beschriftung liest ein
                Screenreader hier bislang gar nichts vor. */}
            <kbd className="rounded border border-border px-1 py-0.5 font-mono text-[10px]">
              <span className="sr-only">{t('context.switcher.keyEnter')}</span>
              <CornerDownLeft aria-hidden="true" className="h-2.5 w-2.5" />
            </kbd>
            {t('context.switcher.hintSelect')}
          </span>
        </span>
        <button
          type="button"
          className="min-h-11 font-medium text-primary hover:underline md:min-h-0"
          onClick={() => {
            // restoreFocus: false (M7) -- der Fokus-Restore-auf-die-Pille war
            // per requestAnimationFrame geplant und griff noch NACH dem
            // Routenwechsel zu /settings, riss den Fokus von der Zielseite
            // zurueck auf eine Pille, die dort gar nicht mehr sichtbar ist.
            onClose({ restoreFocus: false });
            navigate('/settings?tab=accounts');
          }}
        >
          {t('context.switcher.manageAccounts')}
        </button>
      </div>
    </div>
  );
};
